import { formatVolumeDelta, resolveZoneColor } from '../utils/planAnalytics';

const deltaClass = (delta) => {
  if (!delta || delta.minutes == null || delta.minutes === 0) return 'text-gray-600';
  return delta.minutes > 0 ? 'text-emerald-700' : 'text-amber-800';
};

export function VolumeDeltaBadge({ delta }) {
  const label = formatVolumeDelta(delta);
  if (!label) return null;
  return (
    <span className={`font-medium ${deltaClass(delta)}`}>
      {delta?.minutes > 0 ? '↑ ' : delta?.minutes < 0 ? '↓ ' : ''}
      {label}
    </span>
  );
}

export function ZoneMixBar({ totals, compact = false }) {
  if (!totals?.zonePcts?.length) return null;
  return (
    <div className={compact ? '' : 'space-y-1.5'}>
      <div className={`flex overflow-hidden rounded ${compact ? 'h-2' : 'h-3'} bg-gray-200`}>
        {totals.zonePcts.map((zone, index) => (
          <div
            key={zone.key}
            className="h-full"
            style={{ width: `${Math.max(zone.pct, 0)}%`, backgroundColor: resolveZoneColor(zone, index) }}
            title={`${zone.label} ${zone.pct}%`}
          />
        ))}
      </div>
      <div className={`flex flex-wrap gap-x-3 gap-y-0.5 ${compact ? 'text-[10px]' : 'text-xs'} text-gray-600`}>
        {totals.zonePcts.map((zone, index) => (
          <span key={zone.key} className="inline-flex items-center gap-1">
            <span
              className="inline-block w-2 h-2 rounded-sm"
              style={{ backgroundColor: resolveZoneColor(zone, index) }}
            />
            {zone.label} {zone.pct}%
            {!compact && <span className="text-gray-400">({zone.minutes}')</span>}
          </span>
        ))}
      </div>
    </div>
  );
}

export function WeekZoneFooter({ totals, delta, zoneLoggingEnabled }) {
  if (!totals || totals.sessionCount === 0) return null;
  return (
    <div className="flex flex-col gap-1.5 px-3 py-2 bg-gray-50 border-t text-xs text-gray-600">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
        <span className="font-semibold text-gray-700">
          Week total: {totals.totalMinutes}' · {totals.sessionCount} session{totals.sessionCount !== 1 ? 's' : ''}
        </span>
        <VolumeDeltaBadge delta={delta} />
      </div>
      {zoneLoggingEnabled && totals.zonePcts.length > 0 && (
        <ZoneMixBar totals={totals} compact />
      )}
      {zoneLoggingEnabled && (totals.excludedZones?.length > 0 || totals.unzoned > 0) && (
        <div className="flex flex-wrap gap-x-3 text-gray-400">
          {totals.excludedZones.map((zone) => (
            <span key={zone.key}>
              {zone.label} {zone.minutes}' <span className="italic">not in %</span>
            </span>
          ))}
          {totals.unzoned > 0 && (
            <span>
              Unzoned {totals.unzoned}'
            </span>
          )}
        </div>
      )}
    </div>
  );
}

// Athlete-facing training-block card: focused week mix + nearby week volume trend.
export default function TrainingBlockAnalytics({
  weeks = [],
  focusWeekStart,
  zoneLoggingEnabled = false
}) {
  const focus = weeks.find((week) => week.weekStart === focusWeekStart);
  if (!focus || focus.totals.sessionCount === 0) return null;

  return (
    <div className="mb-4 sm:mb-6 bg-white rounded-lg shadow-sm border border-amber-200 overflow-hidden">
      <div className="px-3 sm:px-4 py-3 bg-amber-50 border-b border-amber-200">
        <h3 className="text-sm sm:text-base font-semibold text-amber-950">This week's training</h3>
        <p className="text-xs text-amber-800 mt-0.5">
          {focus.weekStart} – {focus.weekEnd}
        </p>
      </div>
      <div className="p-3 sm:p-4 space-y-3">
        <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1">
          <p className="text-2xl font-bold text-gray-900">
            {focus.totals.totalMinutes}'
            <span className="ml-2 text-sm font-normal text-gray-500">
              {focus.totals.sessionCount} session{focus.totals.sessionCount !== 1 ? 's' : ''}
            </span>
          </p>
          <VolumeDeltaBadge delta={focus.delta} />
        </div>

        {zoneLoggingEnabled && focus.totals.zonePcts.length > 0 && (
          <div>
            <p className="text-xs font-semibold text-gray-600 mb-1.5">% by zone</p>
            <ZoneMixBar totals={focus.totals} />
            {focus.totals.excludedZones.length > 0 && (
              <p className="text-xs text-gray-500 mt-1.5">
                Not in mix:{' '}
                {focus.totals.excludedZones.map((zone) => `${zone.label} ${zone.minutes}'`).join(', ')}
              </p>
            )}
          </div>
        )}

        {weeks.length > 1 && (
          <div>
            <p className="text-xs font-semibold text-gray-600 mb-1.5">Block volume</p>
            <ul className="space-y-1">
              {weeks.map((week) => {
                const isFocus = week.weekStart === focus.weekStart;
                return (
                  <li
                    key={week.weekStart}
                    className={`flex flex-wrap items-center justify-between gap-x-3 gap-y-0.5 text-xs rounded px-2 py-1 ${
                      isFocus ? 'bg-amber-50 font-medium text-amber-950' : 'text-gray-600'
                    }`}
                  >
                    <span>
                      {week.weekStart.slice(0, 5)}–{week.weekEnd.slice(0, 5)}
                      <span className="ml-2">{week.totals.totalMinutes}'</span>
                    </span>
                    <VolumeDeltaBadge delta={week.delta} />
                  </li>
                );
              })}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}
